import React, { useCallback, useState } from 'react'
import styled, { css } from 'styled-components'
import { arrayOf, func, shape, string } from 'prop-types'
import { components } from 'react-select'
import AsyncSelect from 'react-select/async'
import { ReactComponent as ChevronDown } from '../../assets/svg/down.svg'
import { ReactComponent as ChevronUp } from '../../assets/svg/up.svg'
import typography from '../../styles/typography'

function Select({ placeholder, options, onChange, size }) {
	const [opened, setOpened] = useState(false)

	const onMenuOpen = useCallback(() => !opened && setOpened(true), [opened])
	const onMenuClose = useCallback(() => opened && setOpened(false), [opened])

	const filter = (inputValue) =>
		options.filter(
			(opt) =>
				opt.label && opt.label.toLowerCase().includes(inputValue.toLowerCase())
		)

	const promiseOptions = (inputValue) =>
		new Promise((resolve) => {
			setTimeout(() => {
				resolve(filter(inputValue))
			}, 1000)
		})

	const DropdownIndicator = (props) => (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<components.DropdownIndicator {...props}>
			<ChevronDownIcon size={size} />
		</components.DropdownIndicator>
	)

	return (
		<Block size={size}>
			<StyledSelect
				cacheOptions
				defaultOptions
				loadOptions={promiseOptions}
				placeholder={placeholder}
				onChange={onChange}
				classNamePrefix="react-select"
				isSearchable={false}
				components={{ DropdownIndicator }}
				onMenuOpen={onMenuOpen}
				onMenuClose={onMenuClose}
				size={size}
			/>
			<Opened opened={opened} onClick={onMenuClose} size={size}>
				Select a category
				<ChevronUpIcon size={size} />
			</Opened>
		</Block>
	)
}

Select.propTypes = {
	options: arrayOf(
		shape({
			label: string.isRequired,
			value: string.isRequired,
		})
	).isRequired,
	placeholder: string,
	onChange: func,
	size: string,
}

Select.defaultProps = {
	placeholder: '',
	onChange: () => undefined,
	size: 'm',
}

const valueContainerStyle = css`
	box-sizing: border-box;
	font-weight: 500;
	padding: 0 16px;

	${({ size }) =>
		({
			l: typography.label.xl,
			m: typography.label.l,
			s: typography.label.l,
			xs: css`
				${typography.label.m};
				padding: 0 12px;
			`,
		}[size])}
`

const Block = styled.div`
	--select-hight: ${({ size }) =>
		({
			l: '64px',
			m: '56px',
			s: '48px',
			xs: '36px',
		}[size])};
	height: var(--select-hight);
	width: 100%;
	position: relative;
`

const StyledSelect = styled(AsyncSelect)`
	.react-select__control {
		border-radius: 16px;
		width: 100%;
		height: var(--select-hight);
		min-height: var(--select-hight);
		box-sizing: border-box;
		box-shadow: none;
		border: none;
		background-color: ${({ theme }) => theme.colors.gray[50]};

		&:hover {
			border: none;
		}
	}

	.react-select__value-container {
		${valueContainerStyle};
	}

	.react-select__placeholder {
		margin: 0;
		color: ${({ theme }) => theme.colors.gray[400]};
	}

	.react-select__indicator-separator {
		display: none;
	}

	.react-select__menu {
		border: 1px solid ${({ theme }) => theme.colors.gray[100]};
		box-shadow: none;
		border-radius: 16px;
		top: 0;
		margin: 0;
		padding-top: ${({ size }) =>
			({
				l: '54px',
				m: '52px',
				s: '44px',
				xs: '28px',
			}[size])};
	}

	.react-select__menu-list {
		margin: 16px;
		margin-top: 4px;
		padding: 0;
		max-height: 400px;

		${({ size }) =>
			({
				s: 'margin: 8px 16px',
				xs: 'margin: 12px',
			}[size])};
	}

	.react-select__option {
		border-radius: 8px;
		background-color: white;
		color: ${({ theme }) => theme.colors.gray[700]};
		padding: 8px 16px;

		${({ size }) =>
			({
				l: css`
					padding: 12px 16px;
					${typography.label.xl}
				`,
				m: typography.label.l,
				s: typography.label.m,
				xs: css`
					padding: 8px;
					${typography.label.s}
				`,
			}[size])};

		&:hover {
			background-color: ${({ theme }) => theme.colors.gray[50]};
		}
		&:active {
			background-color: transparent;
		}
	}

	.react-select__option--is-selected {
		color: ${({ theme }) => theme.colors.blue[400]} !important;
	}
`

const ChevronDownIcon = styled(ChevronDown)`
	right: 16px;
	padding: 6px;
	box-sizing: border-box;
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	${({ size }) =>
		({
			l: 'padding: 4px',
			xs: 'right: 12px',
		}[size])};
`

const ChevronUpIcon = ChevronDownIcon.withComponent(ChevronUp)

const Opened = styled.div`
	${valueContainerStyle}
	width: 100%;
	height: var(--select-hight);
	position: absolute;
	top: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	z-index: 3;
	display: ${({ opened }) => (opened ? 'flex' : 'none')};
	color: ${({ theme }) => theme.colors.gray[400]};
`

export default React.memo(Select)
